import {
  format,
  parseISO,
  eachDayOfInterval,
  addDays,
  isBefore,
  differenceInCalendarDays,
} from "date-fns";
import type {
  ItineraryLine,
  PricingRequestDetailWithGprtContext,
  QuotedItineraryItem,
} from "../ActionApis/types";

// Names the room in messages ("Junior Studio Suite", not "JSTE").
type RoomTypeLabeller = (roomTypeCode: string) => string;

// Default for callers with no catalogue to hand.
const codeAsLabel: RoomTypeLabeller = (roomTypeCode) => roomTypeCode;

const itineraryKey = (it: ItineraryLine): string =>
  `${it.checkinDate}|${it.checkoutDate}|${it.roomCategory}`;

// Shared by every "is this line ready to submit" check across the create,
// recalculate, and manual-rate flows — callers needing extra fields (e.g.
// a price > 0) compose on top of this rather than re-deriving it.
export const isCompleteLine = (it: ItineraryLine): boolean =>
  Boolean(
    it.checkinDate && it.checkoutDate && it.roomCategory && it.roomCount >= 1
  );

// Parses YYYY-MM-DD as local midnight, avoiding UTC-offset day-shift.
export const parseLocalDate = (iso: string): Date => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

// 0 for an incomplete line rather than throwing — callers already treat 0
// nights as "nothing to split" without needing to check dates first.
export const nightsBetween = (
  checkinDate: string,
  checkoutDate: string
): number =>
  checkinDate && checkoutDate
    ? differenceInCalendarDays(
        parseLocalDate(checkoutDate),
        parseLocalDate(checkinDate)
      )
    : 0;

/** The original ask's span — unlike the request window, it survives edits.
 * Null means unrestricted. */
export const originalAskWindow = (
  original: PricingRequestDetailWithGprtContext["requestedItineraries"]
): { minDate: string; maxDate: string } | null => {
  const dates = original
    .flatMap((it) => [it.checkinDate, it.checkoutDate])
    .filter(Boolean)
    .sort();
  return dates.length
    ? { minDate: dates[0], maxDate: dates[dates.length - 1] }
    : null;
};

/** Clamps a room-count input's raw string to a non-negative integer. */
export const parseRoomCountInput = (val: string): number =>
  val === "" ? 0 : Math.max(0, parseInt(val, 10) || 0);

/** Collects the nights into a set first, so a gap between two stays isn't
 * counted and two room types on the same dates aren't counted twice. */
export const countDistinctNights = (lines: ItineraryLine[]): number => {
  const nights = new Set<string>();
  for (const it of lines) {
    if (!it.checkinDate || !it.checkoutDate) continue;
    const firstNight = parseLocalDate(it.checkinDate);
    const lastNight = addDays(parseLocalDate(it.checkoutDate), -1);
    if (isBefore(lastNight, firstNight)) continue;
    for (const night of eachDayOfInterval({
      start: firstNight,
      end: lastNight,
    })) {
      nights.add(format(night, "yyyy-MM-dd"));
    }
  }
  return nights.size;
};

/**
 * Reconstructs approximate requested-itinerary blocks from the winning-rate
 * rows returned by `GET .../itineraries` — the only itinerary-shaped data the
 * GPC detail endpoint exposes for a plain GPC pricing request (the originally
 * submitted per-line breakdown isn't returned anywhere, see
 * PRICING_REQUEST_DETAIL_REDESIGN.md §3.3). Merges consecutive calendar days
 * with the same room type and room count into one block; blackout/unpriced
 * rows (`roomType` null) have no room type to reconstruct with and are
 * dropped, which also breaks contiguity around them.
 */
export const reconstructItineraryLines = (
  items: QuotedItineraryItem[]
): ItineraryLine[] => {
  const byRoomType = new Map<string, QuotedItineraryItem[]>();
  for (const item of items) {
    if (!item.roomType || item.allocatedRooms == null) continue;
    const list = byRoomType.get(item.roomType) ?? [];
    list.push(item);
    byRoomType.set(item.roomType, list);
  }

  const lines: ItineraryLine[] = [];
  for (const [roomType, rows] of Array.from(byRoomType.entries())) {
    const sorted = [...rows].sort((a, b) =>
      a.stayDate.localeCompare(b.stayDate)
    );
    let runStart = sorted[0];
    let runEnd = sorted[0];
    const flushRun = () => {
      lines.push({
        checkinDate: runStart.stayDate,
        checkoutDate: format(
          addDays(parseLocalDate(runEnd.stayDate), 1),
          "yyyy-MM-dd"
        ),
        roomCategory: roomType,
        roomCount: runEnd.allocatedRooms as number,
      });
    };
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];
      const isNextDay =
        format(addDays(parseLocalDate(prev.stayDate), 1), "yyyy-MM-dd") ===
        curr.stayDate;
      const sameCount = curr.allocatedRooms === runEnd.allocatedRooms;
      if (isNextDay && sameCount) {
        runEnd = curr;
      } else {
        flushRun();
        runStart = curr;
        runEnd = curr;
      }
    }
    flushRun();
  }

  return lines.sort((a, b) => a.checkinDate.localeCompare(b.checkinDate));
};

/**
 * Drops any itinerary row whose (checkin, checkout) date range is fully
 * contained within another row's range for the same room type — e.g. a
 * request whose true itinerary is one 3-night 18th–20th line should never
 * also show redundant 18th–18th / 19th–19th sub-ranges alongside it, even if
 * the source data contains them. Exact-duplicate ranges collapse to the
 * first occurrence. Two rows that only partially overlap (neither contains
 * the other), or that cover the same dates with a different room type, are
 * left alone — those are genuinely distinct lines, not redundant ones.
 * Checkout is treated as exclusive, matching every other date-range
 * comparison in this file — ISO "yyyy-MM-dd" strings sort lexicographically,
 * so plain string comparison is enough without parsing dates.
 */
export const dedupeContainedItineraryRanges = <
  T extends {
    checkinDate: string;
    checkoutDate: string;
    roomTypeText?: string | null;
  }
>(
  items: T[]
): T[] =>
  items.filter((item, idx) => {
    const isContainedIn = (other: T) =>
      other.checkinDate <= item.checkinDate &&
      other.checkoutDate >= item.checkoutDate &&
      (other.roomTypeText ?? null) === (item.roomTypeText ?? null);
    return !items.some((other, otherIdx) => {
      if (otherIdx === idx || !isContainedIn(other)) return false;
      const otherIsStrictlyLarger =
        other.checkinDate < item.checkinDate ||
        other.checkoutDate > item.checkoutDate;
      // Identical range as `other` — keep only the earlier-indexed one.
      return otherIsStrictlyLarger || otherIdx < idx;
    });
  });

/** Returns true if any two complete itinerary lines share the same (checkin, checkout, roomCategory). */
export const hasDuplicateLines = (itineraries: ItineraryLine[]): boolean => {
  const seen = new Set<string>();
  for (const it of itineraries) {
    if (!it.checkinDate || !it.checkoutDate) continue;
    const key = itineraryKey(it);
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
};

/** Blocking errors (overlapping date ranges within the same room type) that prevent submission. */
export const computeErrors = (
  itineraries: ItineraryLine[],
  labelOf: RoomTypeLabeller = codeAsLabel
): string[] => {
  const errors: string[] = [];

  const completeItineraries = itineraries.filter(isCompleteLine);

  const byCategory = new Map<string, { checkin: Date; checkout: Date }[]>();
  for (const it of completeItineraries) {
    const key = it.roomCategory;
    if (!byCategory.has(key)) byCategory.set(key, []);
    byCategory.get(key)!.push({
      checkin: parseISO(it.checkinDate),
      checkout: parseISO(it.checkoutDate),
    });
  }

  for (const [category, ranges] of Array.from(byCategory.entries())) {
    const sorted = [...ranges].sort(
      (a, b) => a.checkin.getTime() - b.checkin.getTime()
    );
    for (let i = 0; i < sorted.length; i++) {
      for (let j = i + 1; j < sorted.length; j++) {
        const a = sorted[i];
        const b = sorted[j];
        // Sorted by checkin, so once b.checkin >= a.checkout no later j can overlap.
        if (!isBefore(b.checkin, a.checkout)) break;
        const overlapStart = format(b.checkin, "yyyy-MM-dd");
        const overlapEnd = format(
          isBefore(a.checkout, b.checkout) ? a.checkout : b.checkout,
          "yyyy-MM-dd"
        );
        errors.push(
          `Overlapping dates for ${labelOf(
            category
          )} from ${overlapStart} to ${overlapEnd}.`
        );
      }
    }
  }

  return errors;
};

/** Non-blocking validation warnings for a set of itineraries. */
export const computeWarnings = (
  itineraries: ItineraryLine[],
  labelOf: RoomTypeLabeller = codeAsLabel
): string[] => {
  const msgs: string[] = [];
  const completeItineraries = itineraries.filter(isCompleteLine);
  if (completeItineraries.length === 0) return msgs;

  // 0. Check-out before check-in
  for (let i = 0; i < completeItineraries.length; i++) {
    const it = completeItineraries[i];
    if (it.checkoutDate < it.checkinDate) {
      msgs.push(
        `Itinerary ${i + 1}: check-out (${
          it.checkoutDate
        }) is before check-in (${it.checkinDate}).`
      );
    }
  }

  // 1. Duplicate lines (same checkin + checkout + room type) violate a backend unique constraint.
  const dupesSeen = new Set<string>();
  const dupesReported = new Set<string>();
  for (const it of completeItineraries) {
    const key = itineraryKey(it);
    if (dupesSeen.has(key) && !dupesReported.has(key)) {
      msgs.push(
        `Duplicate itinerary: ${labelOf(it.roomCategory)} from ${
          it.checkinDate
        } to ${
          it.checkoutDate
        } appears more than once. Merge the room counts into a single line.`
      );
      dupesReported.add(key);
    }
    dupesSeen.add(key);
  }

  return msgs;
};
